import { useSetSettings, useSigma } from '@react-sigma/core';
import { useEffect } from 'react';

import { EDGE_FADE_COLOR, NODE_FADE_COLOR } from '../../../constants/GraphRagConstants';
import { useDebounce } from '../../../hooks/useDebounce';
import { drawHover, drawLabel } from '../canvas-utils';

type GraphSettingsControllerProps = {
  hoveredNode: string | null;
  showLabels: boolean;
  showEdgeLabels: boolean;
};

export const GraphSettingsController = ({
  hoveredNode,
  showLabels,
  showEdgeLabels,
}: GraphSettingsControllerProps) => {
  const sigma = useSigma();
  const setSettings = useSetSettings();
  const graph = sigma.getGraph();

  const debouncedHoveredNode = useDebounce(hoveredNode ?? '', 40);

  useEffect(() => {
    const hoveredColor: string = debouncedHoveredNode
      ? sigma.getNodeDisplayData(debouncedHoveredNode)?.color || ''
      : '';

    setSettings({
      renderLabels: showLabels,
      renderEdgeLabels: showEdgeLabels,

      // Custom canvas rendering
      defaultDrawNodeLabel: drawLabel,
      defaultDrawNodeHover: drawHover,

      // General sigma styling
      labelRenderedSizeThreshold: 0,
      edgeLabelSize: 12,
      edgeLabelColor: { color: '#444' },
      defaultNodeColor: '#999',
      defaultEdgeColor: '#ccc',
      labelSize: 13,
      labelWeight: 'bold',
      labelDensity: 0.07,
      labelGridCellSize: 60,
      zIndex: true,

      nodeReducer: (node: string, data: Record<string, unknown>) => {
        const res: Record<string, unknown> = { ...data };

        if (data.hidden) return { ...res, hidden: true };

        // Hover highlighting
        if (debouncedHoveredNode) {
          const isConnected =
            node === debouncedHoveredNode ||
            graph.hasEdge(node, debouncedHoveredNode) ||
            graph.hasEdge(debouncedHoveredNode, node);

          if (isConnected) return { ...res, zIndex: 1 };

          return {
            ...res,
            zIndex: 0,
            label: '',
            color: NODE_FADE_COLOR,
            highlighted: false,
          };
        }

        // Dim/highlight modes
        if (data.dimmed) {
          return { ...res, color: NODE_FADE_COLOR, label: '', zIndex: 0 };
        }

        if (data.highlighted) {
          return { ...res, size: (data.size as number) * 1.5, zIndex: 2 };
        }

        return res;
      },

      edgeReducer: (edge: string, data: Record<string, unknown>) => {
        const res: Record<string, unknown> = { ...data };

        if (data.hidden) return { ...res, hidden: true };

        if (debouncedHoveredNode) {
          if (graph.hasExtremity(edge, debouncedHoveredNode)) {
            return { ...res, color: hoveredColor, size: 3, zIndex: 1 };
          }
          return { ...res, color: EDGE_FADE_COLOR, hidden: true };
        }

        if (data.dimmed) return { ...res, color: EDGE_FADE_COLOR, zIndex: 0, label: '' };

        if (data.highlighted) {
          return {
            ...res,
            size: 4,
            zIndex: 2,
            label: res.label,
          };
        }

        return res;
      },
    });
  }, [sigma, graph, setSettings, debouncedHoveredNode, showLabels, showEdgeLabels]);

  return null;
};
